import {
  Alert,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiWallet } from '../../../../api/hexa/ApiWallet';

import { useNavigate } from 'react-router';
import { Wallet } from '../../../../api/hexa/models/WalletModels';

interface WalletListProps {
  customerId: string;
}

const WalletList = ({ customerId }: WalletListProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Wallet[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const navigate = useNavigate();
  /**
   * Define the columns for the DataGrid
   */
  const columns = useMemo<GridColDef<Wallet>[]>(
    () => [
      { field: 'id', headerName: 'ID', flex: 1, maxWidth: 100 },
      { field: 'credit', headerName: 'Credit Type', flex: 2 },
      { field: 'balance', headerName: 'Balance', flex: 2 },
      { field: 'on_demand', headerName: 'Flat', flex: 1, type: 'boolean' },
      {
        field: 'created',
        headerName: 'Creation Date',
        type: 'date',
        flex: 1,
        valueGetter: (value, row) => (row.created ? new Date(row.created) : ''),
        renderCell: (params: any) =>
          new Date(params.row.created).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    []
  );

  /**
   * Fetch wallets from the API and set the rows state
   */
  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const { total, wallets } = await ApiWallet.index(
        customerId,
        {
          page: paginationModel.page,
          size: paginationModel.pageSize,
          sort_field: sortModel[0]?.field ?? 'id',
          sort_direction: sortModel[0]?.sort ?? 'asc',
        }
      );
      setRowCount(total);
      setRows(wallets);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the wallets: 
        ${error.response?.data.error ?? 'Error fetching the wallets'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    customerId
  ]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets, customerId]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  return (
    <>
      <Stack spacing={1}>
        <Typography variant={'h4'} sx={{ paddingY: '0.8rem' }}>
          {t('wallets.title')}
        </Typography>
        <Stack
          direction="row"
          pb={'0.5rem'}
          pr={'0.5rem'}
          justifyContent="flex-end"
        >
        </Stack>
        <DataGridPremium
          getRowId={(row) => row.id}
          onRowClick={(params) => {
            navigate(`./wallets/${params.row.id}`, {
              state: { wallet: params.row }
            });
            window.scrollTo(0, 0);
          }}
          loading={loading}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          paginationMode="server"
          pagination={true}
          pageSizeOptions={[10, 25, 50, 100]}
          autoHeight={true}
          rows={rows}
          columns={columns}
          density={'compact'}
          rowHeight={55}
          sx={{
            marginX: '0.5rem !important',
            '& .MuiDataGrid-cell': {
              overflowY: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-cell--textCenter': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Stack>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WalletList;
